/**
 * The one button. Pill-shaped (brand). `ink` is the primary action, at most one per view; `soft` the everyday one;
 * `ghost` and `line` the quiet ones; `danger` only inside a confirm, never as a first click.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from './Icon'
import s from './Button.module.css'

type Native = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'style' | 'children'>

export interface ButtonProps extends Native {
  variant?: 'soft' | 'ink' | 'ghost' | 'line' | 'danger'
  size?: 'md' | 'sm' | 'xs'
  icon?: IconName
  /** Grow to fill its row (phone action bars). */
  fill?: boolean
  children: ReactNode
}

export function Button({ variant = 'soft', size = 'md', icon, fill, type = 'button', children, ...rest }: ButtonProps) {
  return (
    <button {...rest} type={type} className={cx(s.btn, s[variant], s[size], fill && s.fill)}>
      {icon && <Icon name={icon} size="sm" />}
      {children}
    </button>
  )
}

/** A round button with only an icon. The label is required: it is what a screen reader says and the tooltip. */
export function IconButton({ icon, label, variant = 'ghost', size = 'md', ...rest }: Native & { icon: IconName; label: string; variant?: ButtonProps['variant']; size?: 'md' | 'sm' }) {
  return (
    <button {...rest} type="button" aria-label={label} title={label} className={cx(s.btn, s[variant], s.iconOnly, size === 'sm' && s.iconSm)}>
      <Icon name={icon} size={size === 'sm' ? 'sm' : 'md'} />
    </button>
  )
}
